import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from './Header';
import { Footer } from './Footer';
import { Button } from './ui/button';
import {
  Users,
  Shield,
  UserCog,
  Search,
  ArrowUpDown,
  Calendar,
  Mail,
  Building2,
  User as UserIcon,
  CheckCircle,
  XCircle,
  Trash2,
  Percent,
  QrCode,
  Camera,
  ClipboardPaste,
  Ticket,
  Gift,
  MessageSquare
} from 'lucide-react';
import { Html5Qrcode } from 'html5-qrcode';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://nextgenbusinesscup.com/api';

export const AdminPanel = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('created_at'); // 'created_at', 'name', 'email'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' or 'desc'
  const [filterRole, setFilterRole] = useState('all'); // 'all', 'admin', 'manager', 'ticket_controller', 'user'
  const [discountCodes, setDiscountCodes] = useState([]);
  const [newCode, setNewCode] = useState('');
  const [newCodeUniversity, setNewCodeUniversity] = useState('');
  const [discountError, setDiscountError] = useState('');
  const [adminTab, setAdminTab] = useState('users'); // 'users' | 'rabatt' | 'ticket'
  const [ticketVerifyResult, setTicketVerifyResult] = useState(null);
  const [ticketVerifyLoading, setTicketVerifyLoading] = useState(false);
  const [ticketManualInput, setTicketManualInput] = useState('');
  const [scannerActive, setScannerActive] = useState(false);
  const qrScannerRef = React.useRef(null);
  const lastScannedTokenRef = React.useRef(null);
  const [scannerMountKey, setScannerMountKey] = useState(0);

  const [adminTickets, setAdminTickets] = useState([]);
  const [grantUserId, setGrantUserId] = useState('');
  const [grantCategory, setGrantCategory] = useState('sportler');
  const [grantError, setGrantError] = useState('');
  const [grantSuccess, setGrantSuccess] = useState('');
  const [revokeConfirmTicketId, setRevokeConfirmTicketId] = useState(null);
  const [revokeLoading, setRevokeLoading] = useState(false);
  const [revokeError, setRevokeError] = useState('');

  // Contact form submissions
  const [contactSubmissions, setContactSubmissions] = useState([]);
  const [contactLoading, setContactLoading] = useState(false);
  const [contactError, setContactError] = useState('');

  // Nur Admins, Manager oder Ticket-Controller dürfen ins Panel (Ticket-Controller nur Ticket-Kontrolle)
  useEffect(() => {
    if (!user?.is_admin && !user?.is_manager && !user?.is_ticket_controller) {
      navigate('/dashboard');
    }
    if (user?.is_ticket_controller && !user?.is_admin && !user?.is_manager) {
      setAdminTab('ticket');
    }
    if (user?.is_manager && !user?.is_admin && !user?.is_ticket_controller && adminTab === 'users') {
      setAdminTab('ticket');
    }
  }, [user, navigate]);

  // Lade User-Liste (nur für Admins) und Rabattcodes (nur für Admins)
  useEffect(() => {
    if (user?.is_admin) {
      fetchUsers();
      fetchContactSubmissions();
    }
    if (user?.is_admin || user?.is_manager) fetchDiscountCodes();
  }, [user?.is_admin, user?.is_manager]);

  const fetchAdminTickets = async () => {
    if (!user?.is_admin) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/admin/tickets`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAdminTickets(res.data || []);
    } catch (_) {
      setAdminTickets([]);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/admin/users`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setUsers(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || t('admin.fehlerLadenUser'));
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDiscountCodes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/admin/discount-codes`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setDiscountCodes(response.data || []);
    } catch (err) {
      console.error('Error fetching discount codes:', err);
    }
  };

  const updateUserRole = async (userId, roleUpdate) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API_URL}/admin/users/${userId}/role`,
        roleUpdate,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      // Aktualisiere die User-Liste
      await fetchUsers();
    } catch (err) {
      alert(err.response?.data?.detail || t('admin.fehlerRolle'));
      console.error('Error updating role:', err);
    }
  };

  const deleteUser = async (userId, userName) => {
    // Bestätigungsdialog
    const confirmed = window.confirm(
      t('admin.confirmDelete', { name: userName })
    );
    
    if (!confirmed) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_URL}/admin/users/${userId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      // Aktualisiere die User-Liste
      await fetchUsers();
      alert(t('admin.userGeloescht'));
    } catch (err) {
      alert(err.response?.data?.detail || t('admin.fehlerLoeschen'));
      console.error('Error deleting user:', err);
    }
  };

  const createDiscountCode = async (e) => {
    e.preventDefault();
    try {
      setDiscountError('');
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/admin/discount-codes`,
        {
          code: newCode,
          university: newCodeUniversity
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      setNewCode('');
      setNewCodeUniversity('');
      await fetchDiscountCodes();
    } catch (err) {
      console.error('Error creating discount code:', err);
      setDiscountError(err.response?.data?.detail || t('admin.fehlerRabatt'));
    }
  };

  const toggleDiscountCodeActive = async (code, active) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API_URL}/admin/discount-codes/${code}`,
        { active },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      await fetchDiscountCodes();
    } catch (err) {
      console.error('Error updating discount code:', err);
      alert(err.response?.data?.detail || t('admin.fehlerRabattUpdate'));
    }
  };

  const verifyTicket = async (qrToken) => {
    const token = (typeof qrToken === 'string' ? qrToken : ticketManualInput).trim();
    if (!token) return;
    setTicketVerifyLoading(true);
    setTicketVerifyResult(null);
    try {
      const authToken = localStorage.getItem('token');
      const res = await axios.post(
        `${API_URL}/admin/tickets/verify`,
        { qr_token: token },
        { headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' } }
      );
      const data = res.data && typeof res.data === 'object' ? res.data : {};
      setTicketVerifyResult({
        valid: !!data.valid,
        name: data.name ?? null,
        ticket_type: data.ticket_type ?? null,
        message: data.message ?? t('admin.ticketGeprueftMsg'),
      });
      setTicketManualInput('');
      // Kamera läuft weiter – Nutzer kann weiter scannen oder "Kamera beenden" klicken
    } catch (err) {
      setTicketVerifyResult({
        valid: false,
        name: null,
        ticket_type: null,
        message: err.response?.data?.detail || t('admin.fehlerPruefen'),
      });
    } finally {
      setTicketVerifyLoading(false);
    }
  };

  const clearTicketResult = () => {
    setTicketVerifyResult(null);
    lastScannedTokenRef.current = null;
  };

  const revokeTicket = (ticketId) => {
    setRevokeError('');
    setRevokeConfirmTicketId(ticketId);
  };

  const cancelRevokeConfirm = () => {
    setRevokeConfirmTicketId(null);
    setRevokeError('');
  };

  const confirmRevokeTicket = async () => {
    if (!revokeConfirmTicketId) return;
    setRevokeLoading(true);
    setRevokeError('');
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/admin/tickets/${revokeConfirmTicketId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchAdminTickets();
      if (user?.is_admin || user?.is_manager) await fetchDiscountCodes();
      setRevokeConfirmTicketId(null);
    } catch (err) {
      setRevokeError(err.response?.data?.detail || 'Fehler beim Entziehen des Tickets.');
    } finally {
      setRevokeLoading(false);
    }
  };

  const grantTicket = async (e) => {
    e.preventDefault();
    setGrantError('');
    setGrantSuccess('');
    if (!grantUserId.trim()) {
      setGrantError(t('admin.bitteUserAuswaehlen'));
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/admin/tickets/grant`,
        { user_id: grantUserId.trim(), category: grantCategory },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      setGrantSuccess('Gratis-Ticket wurde vergeben. Es wird keine E-Mail versendet.');
      setGrantUserId('');
      await fetchAdminTickets();
    } catch (err) {
      setGrantError(err.response?.data?.detail || 'Fehler beim Vergeben des Tickets');
    }
  };

  // Contact form submissions functions
  const fetchContactSubmissions = async () => {
    if (!user?.is_admin) return;
    setContactLoading(true);
    setContactError('');
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/contact`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setContactSubmissions(res.data || []);
    } catch (err) {
      setContactError(err.response?.data?.detail || 'Error loading contacts');
    } finally {
      setContactLoading(false);
    }
  };

  const toggleContacted = async (submissionId, currentStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API_URL}/contact/${submissionId}/status`,
        { contacted: !currentStatus },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      await fetchContactSubmissions();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error updating status');
    }
  };

  const startScanner = () => {
    setTicketVerifyResult(null);
    lastScannedTokenRef.current = null;
    setScannerMountKey((k) => k + 1);
    setScannerActive(true);
  };

  const stopScanner = () => {
    try {
      if (qrScannerRef.current && typeof qrScannerRef.current.stop === 'function') {
        qrScannerRef.current.stop().catch(() => {});
      }
    } catch (_) {}
    qrScannerRef.current = null;
    lastScannedTokenRef.current = null;
    setScannerActive(false);
  };

  React.useEffect(() => {
    if (!scannerActive || adminTab !== 'ticket') return;
    const readerId = `qr-reader-${scannerMountKey}`;
    const el = document.getElementById(readerId);
    if (!el) return;
    lastScannedTokenRef.current = null;
    const timer = window.setTimeout(() => {
      const el2 = document.getElementById(readerId);
      if (!el2) return;
      const scanner = new Html5Qrcode(readerId);
      qrScannerRef.current = scanner;
      scanner.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        (decodedText) => {
          const token = (decodedText || '').trim();
          if (!token) return;
          if (token === lastScannedTokenRef.current) return;
          lastScannedTokenRef.current = token;
          verifyTicket(token);
        },
        () => {}
      ).catch((err) => {
        console.error('Scanner start error:', err);
        setScannerActive(false);
      });
    }, 350);
    return () => {
      window.clearTimeout(timer);
      try {
        if (qrScannerRef.current && typeof qrScannerRef.current.stop === 'function') {
          qrScannerRef.current.stop().catch(() => {});
        }
      } catch (_) {}
      qrScannerRef.current = null;
    };
  }, [scannerActive, adminTab, scannerMountKey]);

  // Filtere und sortiere Users
  const getFilteredAndSortedUsers = () => {
    let filtered = users.filter(u => {
      // Suchfilter
      const matchesSearch = 
        u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.email.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Rollenfilter
      let matchesRole = true;
      if (filterRole === 'admin') matchesRole = u.is_admin;
      else if (filterRole === 'manager') matchesRole = u.is_manager && !u.is_admin;
      else if (filterRole === 'ticket_controller') matchesRole = u.is_ticket_controller && !u.is_admin && !u.is_manager;
      else if (filterRole === 'user') matchesRole = !u.is_admin && !u.is_manager && !u.is_ticket_controller;
      
      return matchesSearch && matchesRole;
    });

    // Sortierung
    filtered.sort((a, b) => {
      let aVal, bVal;
      
      if (sortBy === 'created_at') {
        aVal = new Date(a.created_at);
        bVal = new Date(b.created_at);
      } else {
        aVal = a[sortBy]?.toLowerCase() || '';
        bVal = b[sortBy]?.toLowerCase() || '';
      }
      
      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    return filtered;
  };

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Nie';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredUsers = getFilteredAndSortedUsers();

  if (!user?.is_admin && !user?.is_manager && !user?.is_ticket_controller) {
    return null;
  }

  const ticketTypeLabel = (type) => {
    const labels = {
      early_bird: 'Early Bird',
      regular: 'Regular',
      late: 'Last Call',
      discount: 'Hochschulrabatt',
      standard: 'Standard',
      sportler: 'Sportler',
      orga_team: 'Orga-Team',
      aussteller: 'Aussteller',
    };
    return labels[type] || type;
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <div className="pt-24 sm:pt-28 md:pt-32 pb-12 sm:pb-20 px-3 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="bg-ngbc-beige rounded-2xl shadow-lg p-8 mb-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-pink-600 rounded-full flex items-center justify-center">
                  <Shield className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-ngbc-dark">{user?.is_admin ? t('admin.userVerwaltung') : user?.is_ticket_controller && !user?.is_manager ? t('admin.ticketKontrolle') : t('admin.ticketRabatt')}</h1>
                </div>
              </div>
              <Button
                onClick={() => navigate('/dashboard')}
                variant="outline"
              >
                {t('admin.zurueckDashboard')}
              </Button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap gap-2 mb-4 sm:mb-6">
            {user?.is_admin && (
              <button
                onClick={() => setAdminTab('users')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                  adminTab === 'users' ? 'bg-ngbc-primary text-white' : 'bg-ngbc-secondary text-ngbc-dark hover:bg-ngbc-secondary'
                }`}
              >
                <Users className="w-4 h-4" />
                {t('admin.userVerwaltungTab')}
              </button>
            )}
            {(user?.is_admin || user?.is_manager) && (
            <button
              onClick={() => setAdminTab('rabatt')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                adminTab === 'rabatt' ? 'bg-ngbc-primary text-white' : 'bg-ngbc-secondary text-ngbc-dark hover:bg-ngbc-secondary'
              }`}
            >
              <Percent className="w-4 h-4" />
              {t('admin.rabattcodes')}
            </button>
            )}
            <button
              onClick={() => { setAdminTab('ticket'); setScannerActive(false); setTicketVerifyResult(null); }}
              className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                adminTab === 'ticket' ? 'bg-ngbc-primary text-white' : 'bg-ngbc-secondary text-ngbc-dark hover:bg-ngbc-secondary'
              }`}
            >
              <QrCode className="w-4 h-4" />
              {t('admin.ticketPruefen')}
            </button>
            {user?.is_admin && (
              <button
                onClick={() => { setAdminTab('tickets'); fetchAdminTickets(); }}
                className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                  adminTab === 'tickets' ? 'bg-ngbc-primary text-white' : 'bg-ngbc-secondary text-ngbc-dark hover:bg-ngbc-secondary'
                }`}
              >
                <Ticket className="w-4 h-4" />
                {t('admin.tickets')}
              </button>
            )}
            {user?.is_admin && (
              <button
                onClick={() => { setAdminTab('contacts'); fetchContactSubmissions(); }}
                className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                  adminTab === 'contacts' ? 'bg-ngbc-primary text-white' : 'bg-ngbc-secondary text-ngbc-dark hover:bg-ngbc-secondary'
                }`}
              >
                <MessageSquare className="w-4 h-4" />
                Contact Requests
              </button>
            )}
          </div>

          {/* Tab: User-Verwaltung (nur Admin) */}
          {adminTab === 'users' && user?.is_admin && (
            <>
          {/* Statistiken */}
          <div className="grid md:grid-cols-5 gap-6 mb-8">
            <div className="bg-ngbc-beige rounded-xl shadow-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-ngbc-dark-text text-sm">{t('admin.gesamt')}</p>
                  <p className="text-3xl font-bold text-ngbc-dark">{users.length}</p>
                </div>
                <Users className="w-10 h-10 text-ngbc-primary" />
              </div>
            </div>
            <div className="bg-ngbc-beige rounded-xl shadow-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-ngbc-dark-text text-sm">{t('admin.admins')}</p>
                  <p className="text-3xl font-bold text-red-600">{users.filter(u => u.is_admin).length}</p>
                </div>
                <Shield className="w-10 h-10 text-red-600" />
              </div>
            </div>
            <div className="bg-ngbc-beige rounded-xl shadow-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-ngbc-dark-text text-sm">{t('admin.manager')}</p>
                  <p className="text-3xl font-bold text-orange-600">{users.filter(u => u.is_manager && !u.is_admin).length}</p>
                </div>
                <UserCog className="w-10 h-10 text-orange-600" />
              </div>
            </div>
            <div className="bg-ngbc-beige rounded-xl shadow-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-ngbc-dark-text text-sm">{t('admin.ticketController')}</p>
                  <p className="text-3xl font-bold text-teal-600">{users.filter(u => u.is_ticket_controller && !u.is_admin && !u.is_manager).length}</p>
                </div>
                <QrCode className="w-10 h-10 text-teal-600" />
              </div>
            </div>
            <div className="bg-ngbc-beige rounded-xl shadow-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-ngbc-dark-text text-sm">{t('admin.user')}</p>
                  <p className="text-3xl font-bold text-ngbc-primary">{users.filter(u => !u.is_admin && !u.is_manager && !u.is_ticket_controller).length}</p>
                </div>
                <UserIcon className="w-10 h-10 text-ngbc-primary" />
              </div>
            </div>
          </div>

          {/* Filter & Suche */}
          <div className="bg-ngbc-beige rounded-xl shadow-lg p-6 mb-8">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-ngbc-muted w-5 h-5" />
                <input
                  type="text"
                  placeholder={t('admin.suchePlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-ngbc-dark-text rounded-lg focus:ring-2 focus:ring-ngbc-primary focus:border-transparent"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setFilterRole('all')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    filterRole === 'all'
                      ? 'bg-ngbc-primary text-white'
                      : 'bg-ngbc-secondary text-ngbc-dark hover:bg-ngbc-secondary'
                  }`}
                >
                  {t('admin.alle')}
                </button>
                <button
                  onClick={() => setFilterRole('admin')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    filterRole === 'admin'
                      ? 'bg-red-600 text-white'
                      : 'bg-ngbc-secondary text-ngbc-dark hover:bg-ngbc-secondary'
                  }`}
                >
                  {t('admin.admins')}
                </button>
                <button
                  onClick={() => setFilterRole('manager')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    filterRole === 'manager'
                      ? 'bg-orange-600 text-white'
                      : 'bg-ngbc-secondary text-ngbc-dark hover:bg-ngbc-secondary'
                  }`}
                >
                  Manager
                </button>
                <button
                  onClick={() => setFilterRole('ticket_controller')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    filterRole === 'ticket_controller'
                      ? 'bg-teal-600 text-white'
                      : 'bg-ngbc-secondary text-ngbc-dark hover:bg-ngbc-secondary'
                  }`}
                >
                  {t('admin.ticketController')}
                </button>
                <button
                  onClick={() => setFilterRole('user')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    filterRole === 'user'
                      ? 'bg-ngbc-primary text-white'
                      : 'bg-ngbc-secondary text-ngbc-dark hover:bg-ngbc-secondary'
                  }`}
                >
                  User
                </button>
              </div>
            </div>
          </div>

          {/* User-Tabelle */}
          <div className="bg-ngbc-beige rounded-xl shadow-lg overflow-hidden">
            {loading ? (
              <div className="p-12 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ngbc-primary mx-auto"></div>
                <p className="mt-4 text-ngbc-dark-text">{t('admin.ladeUser')}</p>
              </div>
            ) : error ? (
              <div className="p-12 text-center">
                <XCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
                <p className="text-red-600">{error}</p>
                <Button onClick={fetchUsers} className="mt-4">
                  {t('admin.erneutVersuchen')}
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-ngbc-secondary/30 border-b border-ngbc-secondary">
                    <tr>
                      <th className="px-6 py-4 text-left">
                        <button
                          onClick={() => toggleSort('name')}
                          className="flex items-center space-x-2 font-semibold text-ngbc-dark hover:text-ngbc-primary"
                        >
                          <span>{t('admin.name')}</span>
                          <ArrowUpDown className="w-4 h-4" />
                        </button>
                      </th>
                      <th className="px-6 py-4 text-left">
                        <button
                          onClick={() => toggleSort('email')}
                          className="flex items-center space-x-2 font-semibold text-ngbc-dark hover:text-ngbc-primary"
                        >
                          <span>E-Mail</span>
                          <ArrowUpDown className="w-4 h-4" />
                        </button>
                      </th>
                      <th className="px-6 py-4 text-left">
                        <span className="font-semibold text-ngbc-dark">{t('admin.typ')}</span>
                      </th>
                      <th className="px-6 py-4 text-left">
                        <button
                          onClick={() => toggleSort('created_at')}
                          className="flex items-center space-x-2 font-semibold text-ngbc-dark hover:text-ngbc-primary"
                        >
                          <span>{t('admin.registriert')}</span>
                          <ArrowUpDown className="w-4 h-4" />
                        </button>
                      </th>
                      <th className="px-6 py-4 text-left">
                        <span className="font-semibold text-ngbc-dark">{t('admin.rollen')}</span>
                      </th>
                      <th className="px-6 py-4 text-left">
                        <span className="font-semibold text-ngbc-dark">{t('admin.aktionen')}</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ngbc-secondary">
                    {filteredUsers.map((u) => (
                      <tr key={u.id} className="hover:bg-ngbc-secondary/30 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-3">
                            {u.user_type === 'company' ? (
                              <Building2 className="w-5 h-5 text-ngbc-primary" />
                            ) : (
                              <UserIcon className="w-5 h-5 text-ngbc-dark-text" />
                            )}
                            <span className="font-medium text-ngbc-dark">{u.name}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-2 text-ngbc-dark-text">
                            <Mail className="w-4 h-4" />
                            <span>{u.email}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            u.user_type === 'company'
                              ? 'bg-purple-100 text-purple-700'
                              : 'bg-blue-100 text-ngbc-accent'
                          }`}>
                            {u.user_type === 'company' ? t('admin.firma') : t('admin.userType')}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-2 text-ngbc-dark-text text-sm">
                            <Calendar className="w-4 h-4" />
                            <span>{formatDate(u.created_at)}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-wrap gap-2">
                            {u.is_admin && (
                              <span className="px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700 flex items-center gap-1">
                                <Shield className="w-3 h-3" />
                                {t('admin.admin')}
                              </span>
                            )}
                            {u.is_manager && (
                              <span className="px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700 flex items-center gap-1">
                                <UserCog className="w-3 h-3" />
                                {t('admin.manager')}
                              </span>
                            )}
                            {u.is_ticket_controller && (
                              <span className="px-3 py-1 rounded-full text-xs font-medium bg-teal-100 text-teal-700 flex items-center gap-1">
                                <QrCode className="w-3 h-3" />
                                {t('admin.ticketController')}
                              </span>
                            )}
                            {!u.is_admin && !u.is_manager && !u.is_ticket_controller && (
                              <span className="px-3 py-1 rounded-full text-xs font-medium bg-ngbc-secondary text-ngbc-dark">
                                {t('admin.user')}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-wrap gap-2">
                            {!u.is_admin && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => updateUserRole(u.id, { is_admin: true })}
                                className="text-xs"
                              >
                                <Shield className="w-3 h-3 mr-1" />
                                {t('admin.admin')}
                              </Button>
                            )}
                            {u.is_admin && u.id !== user?.id && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => updateUserRole(u.id, { is_admin: false })}
                                className="text-xs text-red-600 hover:text-red-700"
                              >
                                <XCircle className="w-3 h-3 mr-1" />
                                {t('admin.adminEntfernen')}
                              </Button>
                            )}
                            {!u.is_manager && !u.is_admin && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => updateUserRole(u.id, { is_manager: true })}
                                className="text-xs"
                              >
                                <UserCog className="w-3 h-3 mr-1" />
                                {t('admin.manager')}
                              </Button>
                            )}
                            {u.is_manager && !u.is_admin && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => updateUserRole(u.id, { is_manager: false })}
                                className="text-xs text-orange-600 hover:text-orange-700"
                              >
                                <XCircle className="w-3 h-3 mr-1" />
                                {t('admin.managerEntfernen')}
                              </Button>
                            )}
                            {!u.is_ticket_controller && !u.is_admin && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => updateUserRole(u.id, { is_ticket_controller: true })}
                                className="text-xs"
                              >
                                <QrCode className="w-3 h-3 mr-1" />
                                {t('admin.ticketController')}
                              </Button>
                            )}
                            {u.is_ticket_controller && !u.is_admin && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => updateUserRole(u.id, { is_ticket_controller: false })}
                                className="text-xs text-teal-600 hover:text-teal-700"
                              >
                                <XCircle className="w-3 h-3 mr-1" />
                                {t('admin.ticketControllerEntfernen')}
                              </Button>
                            )}
                            {u.id !== user?.id && !u.is_admin && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => deleteUser(u.id, u.name)}
                                className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50 border-red-300"
                              >
                                <Trash2 className="w-3 h-3 mr-1" />
                                {t('admin.loeschen')}
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredUsers.length === 0 && (
                  <div className="p-12 text-center text-ngbc-dark-text">
                    {t('admin.keineUser')}
                  </div>
                )}
              </div>
            )}
          </div>
            </>
          )}

          {/* Tab: Rabattcodes */}
          {adminTab === 'rabatt' && (
      <div className="max-w-7xl mx-auto mt-0">
        <div className="bg-ngbc-beige rounded-2xl shadow-lg p-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-ngbc-secondary/30 rounded-lg flex items-center justify-center">
                <Percent className="w-6 h-6 text-ngbc-primary" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-ngbc-dark">{t('admin.rabattcodesTitle')}</h2>
                <p className="text-ngbc-dark-text text-sm">
                  {t('admin.rabattcodesDesc')}
                </p>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mt-8">
            {/* Neuer Rabattcode */}
            <div className="md:col-span-1">
              <h3 className="text-lg font-semibold text-ngbc-dark mb-4">{t('admin.neuenRabattcodeAnlegen')}</h3>
              <form className="space-y-4" onSubmit={createDiscountCode}>
                <div>
                  <label className="block text-sm font-medium text-ngbc-dark mb-1">
                    Rabattcode *
                  </label>
                  <input
                    type="text"
                    value={newCode}
                    onChange={(e) => setNewCode(e.target.value.toUpperCase())}
                    placeholder="z.B. HS-MUENCHEN-2026"
                    className="w-full px-3 py-2 border border-ngbc-dark-text rounded-lg focus:ring-2 focus:ring-ngbc-primary focus:border-transparent text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-ngbc-dark mb-1">
                    {t('admin.hochschuleLabel')}
                  </label>
                  <input
                    type="text"
                    value={newCodeUniversity}
                    onChange={(e) => setNewCodeUniversity(e.target.value)}
                    placeholder={t('admin.hochschulePlaceholder')}
                    className="w-full px-3 py-2 border border-ngbc-dark-text rounded-lg focus:ring-2 focus:ring-ngbc-primary focus:border-transparent text-sm"
                    required
                  />
                </div>
                {discountError && (
                  <p className="text-sm text-red-600">{discountError}</p>
                )}
                <Button type="submit" className="w-full bg-ngbc-primary hover:bg-ngbc-accent">
                  Rabattcode erstellen
                </Button>
              </form>
            </div>

            {/* Übersicht Rabattcodes */}
            <div className="md:col-span-2">
              <h3 className="text-lg font-semibold text-ngbc-dark mb-4">{t('admin.bestehendeRabattcodes')}</h3>
              {discountCodes.length === 0 ? (
                <p className="text-sm text-ngbc-dark-text">
                  Noch keine Rabattcodes angelegt.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-ngbc-secondary">
                    <thead className="bg-ngbc-secondary/30">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-ngbc-muted uppercase tracking-wider">
                          Code
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-ngbc-muted uppercase tracking-wider">
                          Hochschule / Uni
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-ngbc-muted uppercase tracking-wider">
                          Nutzungen
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-ngbc-muted uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-4 py-3" />
                      </tr>
                    </thead>
                    <tbody className="bg-ngbc-beige divide-y divide-ngbc-secondary">
                      {discountCodes.map((code) => (
                        <tr key={code.code}>
                          <td className="px-4 py-3 text-sm font-mono text-ngbc-dark">
                            {code.code}
                          </td>
                          <td className="px-4 py-3 text-sm text-ngbc-dark">
                            {code.university}
                          </td>
                          <td className="px-4 py-3 text-sm text-ngbc-dark">
                            {code.usage_count}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                                code.active
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-ngbc-secondary text-ngbc-dark-text'
                              }`}
                            >
                              {code.active ? 'Aktiv' : 'Deaktiviert'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right text-sm">
                            <button
                              type="button"
                              onClick={() => toggleDiscountCodeActive(code.code, !code.active)}
                              className="text-ngbc-primary hover:text-ngbc-accent text-xs font-medium"
                            >
                              {code.active ? 'Deaktivieren' : 'Aktivieren'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
          </div>
          )}

          {/* Tab: Ticket prüfen (QR-Scan) */}
          {adminTab === 'ticket' && (
      <div className="max-w-7xl mx-auto mt-0 px-0 sm:px-0">
        <div className="bg-ngbc-beige rounded-2xl shadow-lg p-4 sm:p-6 md:p-8">
          <div className="flex flex-wrap items-center gap-3 mb-4 sm:mb-6">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-ngbc-secondary/30 rounded-lg flex items-center justify-center flex-shrink-0">
              <QrCode className="w-5 h-5 sm:w-6 sm:h-6 text-ngbc-primary" />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-xl sm:text-2xl font-bold text-ngbc-dark">{t('admin.ticketPruefen')}</h2>
              <p className="text-ngbc-dark-text text-xs sm:text-sm">
                {t('admin.ticketPruefenDesc')}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
            <div className="min-w-0">
              {!scannerActive ? (
                <div className="space-y-4">
                  <Button
                    type="button"
                    onClick={startScanner}
                    className="w-full bg-ngbc-primary hover:bg-ngbc-accent flex items-center justify-center gap-2 py-6 sm:py-4"
                  >
                    <Camera className="w-5 h-5 flex-shrink-0" />
                    <span>Kamera zum Scannen starten</span>
                  </Button>
                  <p className="text-sm text-ngbc-muted text-center">oder</p>
                  <div>
                    <label className="block text-sm font-medium text-ngbc-dark mb-1">{t('admin.qrInhaltEinfuegen')}</label>
                    <textarea
                      value={ticketManualInput}
                      onChange={(e) => setTicketManualInput(e.target.value)}
                      placeholder={t('admin.qrInhaltPlaceholder')}
                      rows={3}
                      className="w-full px-3 py-2 border border-ngbc-dark-text rounded-lg focus:ring-2 focus:ring-ngbc-primary focus:border-transparent text-sm min-w-0"
                    />
                    <Button
                      type="button"
                      onClick={() => verifyTicket()}
                      disabled={ticketVerifyLoading || !ticketManualInput.trim()}
                      className="mt-2 w-full"
                    >
                      <ClipboardPaste className="w-4 h-4 mr-2 flex-shrink-0" />
                      {ticketVerifyLoading ? '...' : t('admin.pruefen')}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <div id={`qr-reader-${scannerMountKey}`} className="rounded-lg overflow-hidden border border-ngbc-secondary w-full max-w-full" style={{ minHeight: 200 }} />
                  <Button type="button" variant="outline" onClick={stopScanner} className="w-full">
                    {t('admin.kameraBeenden')}
                  </Button>
                </div>
              )}
            </div>

            <div className="min-w-0 flex flex-col">
              {ticketVerifyLoading && (
                <div className="rounded-xl border-2 border-ngbc-secondary p-6 flex items-center justify-center gap-3">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-ngbc-primary border-t-transparent" />
                  <span className="text-ngbc-dark-text">{ticketVerifyLoading ? '...' : ''}</span>
                </div>
              )}
              {ticketVerifyResult && !ticketVerifyLoading && (
                <div className={`rounded-xl border-2 p-4 sm:p-6 flex-1 ${
                  ticketVerifyResult.valid
                    ? 'bg-green-50 border-green-200'
                    : 'bg-red-50 border-red-200'
                }`}>
                  <div className="flex items-start gap-3 mb-4">
                    {ticketVerifyResult.valid ? (
                      <CheckCircle className="w-8 h-8 sm:w-10 sm:h-10 text-green-600 flex-shrink-0 mt-0.5" />
                    ) : (
                      <XCircle className="w-8 h-8 sm:w-10 sm:h-10 text-red-600 flex-shrink-0 mt-0.5" />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className={`text-base sm:text-lg font-bold ${ticketVerifyResult.valid ? 'text-green-800' : 'text-red-800'}`}>
                        {ticketVerifyResult.valid ? t('admin.gueltig') : t('admin.ungueltig')}
                      </p>
                      <p className="text-sm text-ngbc-dark-text break-words">{ticketVerifyResult.message || ''}</p>
                    </div>
                  </div>
                  {ticketVerifyResult.name != null && ticketVerifyResult.name !== '' && (
                    <p className="text-ngbc-dark font-semibold text-sm sm:text-base break-words">
                      <span className="text-ngbc-dark-text">Name: </span>{ticketVerifyResult.name}
                    </p>
                  )}
                  {ticketVerifyResult.ticket_type && (
                    <p className="text-ngbc-dark mt-1 text-sm sm:text-base">
                      <span className="text-ngbc-dark-text">Kategorie: </span>{ticketTypeLabel(ticketVerifyResult.ticket_type)}
                    </p>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="mt-4"
                    onClick={clearTicketResult}
                  >
                    {t('admin.nochmalPruefen')}
                  </Button>
                </div>
              )}
              {!ticketVerifyResult && !ticketVerifyLoading && (
                <div className="rounded-xl border-2 border-dashed border-ngbc-secondary p-6 sm:p-8 text-center text-ngbc-muted text-sm sm:text-base flex items-center justify-center min-h-[180px]">
                  {t('admin.scanErgebnis')}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
          )}

          {/* Tab: Tickets (nur Admin) – Liste, Entziehen, Gratis-Ticket vergeben */}
          {adminTab === 'tickets' && user?.is_admin && (
      <div className="max-w-7xl mx-auto mt-0 px-0 sm:px-0">
        <div className="bg-ngbc-beige rounded-2xl shadow-lg p-4 sm:p-6 md:p-8">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 bg-ngbc-secondary/30 rounded-lg flex items-center justify-center">
              <Ticket className="w-6 h-6 text-ngbc-primary" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-ngbc-dark">{t('admin.ticketsTitle')}</h2>
              <p className="text-ngbc-dark-text text-sm">{t('admin.ticketsDesc')}</p>
            </div>
          </div>

          {/* Gratis-Ticket vergeben */}
          <div className="mb-8 p-6 bg-ngbc-secondary/30 rounded-xl border border-ngbc-secondary">
            <h3 className="text-lg font-semibold text-ngbc-dark mb-4 flex items-center gap-2">
              <Gift className="w-5 h-5 text-ngbc-primary" />
              Gratis-Ticket vergeben
            </h3>
            <p className="text-sm text-ngbc-dark-text mb-4">{t('admin.keineRechnung')}</p>
            <form onSubmit={grantTicket} className="flex flex-wrap items-end gap-4">
              <div className="flex-1 min-w-[200px]">
                <label className="block text-sm font-medium text-ngbc-dark mb-1">{t('admin.userAuswaehlen')}</label>
                <select
                  value={grantUserId}
                  onChange={(e) => setGrantUserId(e.target.value)}
                  className="w-full px-3 py-2 border border-ngbc-dark-text rounded-lg focus:ring-2 focus:ring-ngbc-primary text-sm"
                >
                  <option value="">{t('admin.bitteWaehlen')}</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                  ))}
                </select>
              </div>
              <div className="w-48">
                <label className="block text-sm font-medium text-ngbc-dark mb-1">{t('admin.kategorie')}</label>
                <select
                  value={grantCategory}
                  onChange={(e) => setGrantCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-ngbc-dark-text rounded-lg focus:ring-2 focus:ring-ngbc-primary text-sm"
                >
                  <option value="sportler">Sportler</option>
                  <option value="orga_team">Orga-Team</option>
                  <option value="aussteller">Aussteller</option>
                </select>
              </div>
              <Button type="submit" className="bg-ngbc-primary hover:bg-ngbc-accent">
                Gratis-Ticket vergeben
              </Button>
            </form>
            {grantError && <p className="text-red-600 text-sm mt-2">{grantError}</p>}
            {grantSuccess && <p className="text-green-700 text-sm mt-2">{grantSuccess}</p>}
          </div>

          {/* Ticket-Liste */}
          {(() => {
            const totalTickets = adminTickets.length;
            const redeemedCount = adminTickets.filter((t) => t.redeemed_at).length;
            return (
              <>
                <div className="mb-6 p-4 bg-ngbc-secondary/30 rounded-xl border border-ngbc-secondary">
                  <p className="text-lg font-semibold text-ngbc-dark">
                    <span className="text-ngbc-primary">{redeemedCount}</span>
                    <span className="text-ngbc-dark-text"> / </span>
                    <span className="text-ngbc-dark">{totalTickets}</span>
                    <span className="text-ngbc-dark-text font-normal"> {t('admin.ticketsEingeloest')}</span>
                  </p>
                </div>
          <h3 className="text-lg font-semibold text-ngbc-dark mb-4">{t('admin.alleVergebenenTickets')}</h3>
          {adminTickets.length === 0 ? (
            <p className="text-ngbc-muted text-sm">{t('admin.nochKeineTickets')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-ngbc-secondary">
                <thead className="bg-ngbc-secondary/30">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-ngbc-muted uppercase">{t('admin.eingeloest')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-ngbc-muted uppercase">{t('admin.name')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-ngbc-muted uppercase">{t('admin.email')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-ngbc-muted uppercase">{t('admin.betrag')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-ngbc-muted uppercase">{t('admin.kategorie')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-ngbc-muted uppercase">{t('admin.status')}</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-ngbc-muted uppercase">{t('admin.aktion')}</th>
                  </tr>
                </thead>
                <tbody className="bg-ngbc-beige divide-y divide-ngbc-secondary">
                  {adminTickets.map((ticket) => (
                    <tr key={ticket.id} className={ticket.redeemed_at ? 'border-l-4 border-l-green-500 bg-green-50/50' : ''}>
                      <td className="px-4 py-3 text-sm">
                        {ticket.redeemed_at ? (
                          <span className="inline-flex items-center gap-1 text-green-700 font-medium" title={formatDate(ticket.redeemed_at)}>
                            <CheckCircle className="w-5 h-5 text-green-600" />
                            Ja
                          </span>
                        ) : (
                          <span className="text-ngbc-muted">–</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-ngbc-dark">{ticket.user_name}</td>
                      <td className="px-4 py-3 text-sm text-ngbc-dark-text">{ticket.user_email}</td>
                      <td className="px-4 py-3 text-sm font-medium">{ticket.price_eur.toFixed(2)} €</td>
                      <td className="px-4 py-3 text-sm">{ticketTypeLabel(ticket.ticket_type)}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className={ticket.status === 'valid' ? 'text-green-700 font-medium' : 'text-ngbc-muted'}>{ticket.status === 'valid' ? t('admin.gueltig') : ticket.status}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {ticket.status === 'valid' && (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-300 hover:bg-red-50"
                            onClick={() => revokeTicket(ticket.id)}
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Entziehen
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
              </>
            );
          })()}
        </div>
      </div>
          )}
        </div>
      </div>
      {/* Eigenes Pop-up: Ticket entziehen bestätigen */}
      {revokeConfirmTicketId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={cancelRevokeConfirm}>
          <div
            className="bg-ngbc-beige rounded-xl shadow-xl max-w-md w-full p-6 border border-ngbc-secondary"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-ngbc-dark mb-2">{t('admin.revokeConfirmTitle')}</h3>
            <p className="text-ngbc-dark-text text-sm mb-6">
              {t('admin.revokeConfirmText')}
            </p>
            {revokeError && (
              <p className="text-sm text-red-600 bg-red-50 p-2 rounded mb-4">{revokeError}</p>
            )}
            <div className="flex gap-3 justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={cancelRevokeConfirm}
                disabled={revokeLoading}
              >
                {t('admin.abbrechen')}
              </Button>
              <Button
                type="button"
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={confirmRevokeTicket}
                disabled={revokeLoading}
              >
                {revokeLoading ? t('admin.bitteWarten') : t('admin.ok')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Tab: Contact Requests (Admin only) */}
      {adminTab === 'contacts' && user?.is_admin && (
        <div className="max-w-7xl mx-auto mt-0 px-0 sm:px-0 mb-8">
          <div className="bg-ngbc-beige rounded-2xl shadow-lg p-4 sm:p-6 md:p-8">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-ngbc-secondary/30 rounded-lg flex items-center justify-center">
                <MessageSquare className="w-6 h-6 text-ngbc-primary" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-ngbc-dark">Contact Requests</h2>
                <p className="text-ngbc-dark-text text-sm">Partner inquiries from the contact form</p>
              </div>
            </div>

            {/* Statistics */}
            <div className="mb-6 p-4 bg-ngbc-secondary/30 rounded-xl border border-ngbc-secondary">
              <p className="text-lg font-semibold text-ngbc-dark">
                <span className="text-green-600">{contactSubmissions.filter((c) => c.contacted).length}</span>
                <span className="text-ngbc-dark-text"> / </span>
                <span className="text-ngbc-dark">{contactSubmissions.length}</span>
                <span className="text-ngbc-dark-text font-normal"> contacted</span>
              </p>
            </div>

            {/* Error state */}
            {contactError ? (
              <div className="text-center py-8">
                <XCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
                <p className="text-red-600 mb-4">{contactError}</p>
                <Button onClick={fetchContactSubmissions}>Try Again</Button>
              </div>
            ) : contactLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ngbc-primary mx-auto"></div>
                <p className="mt-4 text-ngbc-dark-text">Loading...</p>
              </div>
            ) : contactSubmissions.length === 0 ? (
              <div className="text-center py-12 text-ngbc-dark-text">
                <MessageSquare className="w-16 h-16 mx-auto mb-4 text-ngbc-muted" />
                <p className="text-lg">No contact requests yet</p>
                <p className="text-sm">Partner inquiries will appear here</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-ngbc-secondary">
                  <thead className="bg-ngbc-secondary/30">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-ngbc-muted uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-ngbc-muted uppercase">Company</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-ngbc-muted uppercase">Email</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-ngbc-muted uppercase">Phone</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-ngbc-muted uppercase">Submitted</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-ngbc-muted uppercase">Action</th>
                    </tr>
                  </thead>
                  <tbody className="bg-ngbc-beige divide-y divide-ngbc-secondary">
                    {contactSubmissions.map((submission) => (
                      <tr key={submission.id} className={submission.contacted ? 'border-l-4 border-l-green-500 bg-green-50/50' : 'border-l-4 border-l-yellow-500'}>
                        <td className="px-4 py-3 text-sm">
                          {submission.contacted ? (
                            <span className="inline-flex items-center gap-1 text-green-700 font-medium">
                              <CheckCircle className="w-5 h-5 text-green-600" />
                              Contacted
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-yellow-700 font-medium">
                              <XCircle className="w-5 h-5 text-yellow-600" />
                              Pending
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-ngbc-dark">{submission.company_name}</td>
                        <td className="px-4 py-3 text-sm">
                          <a href={`mailto:${submission.email}`} className="text-ngbc-primary hover:text-ngbc-accent underline">
                            {submission.email}
                          </a>
                        </td>
                        <td className="px-4 py-3 text-sm text-ngbc-dark-text">
                          {submission.phone || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-ngbc-dark-text">
                          {submission.submitted_at ? new Date(submission.submitted_at).toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          }) : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <Button
                            type="button"
                            size="sm"
                            variant={submission.contacted ? 'outline' : 'default'}
                            className={submission.contacted ? 'border-green-300 text-green-700 hover:bg-green-50' : 'bg-green-600 hover:bg-green-700 text-white'}
                            onClick={() => toggleContacted(submission.id, submission.contacted)}
                          >
                            {submission.contacted ? 'Mark Pending' : 'Mark Contacted'}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};
